import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, AlertTriangle, CheckCircle2, MessageSquareWarning } from "lucide-react";
import { api } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/settings/Section";
import { RevenueChart } from "@/components/settings/RevenueChart";

function Tile({ label, value, tone = "" }) {
  return (
    <div className="border border-border rounded-lg bg-background p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold tabular-nums mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

export function OwnerPanel() {
  const [earn, setEarn] = useState(null);
  const [ov, setOv] = useState(null);
  const load = () => {
    api.get("/owner/stripe-dashboard").then((r) => setEarn(r.data)).catch(() => {});
    api.get("/owner/overview").then((r) => setOv(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const resolve = async (id, status) => { await api.patch(`/owner/tickets/${id}`, { status }); toast.success("Ticket updated"); load(); };
  if (!earn || !ov) return null;
  const healthy = ov.errors_24h === 0 && ov.failed_emails === 0;
  return (
    <Section title="Owner control center" desc="Earnings, payouts, health and support — all automated. You get a daily report by email." testId="owner-panel"
      className="border-indigo-500/40" right={<Wallet className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Available payout" value={money(earn.available_usd)} tone="text-emerald-600 dark:text-emerald-400" />
        <Tile label="Pending" value={money(earn.pending_usd)} />
        <Tile label="Paying users" value={ov.paying_users} />
        <Tile label="Total users" value={ov.users} />
      </div>
      <a data-testid="stripe-onboarding-link" href={earn.onboarding_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-4 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
        Claim your Stripe account & connect your bank for automatic payouts →
      </a>

      <RevenueChart />

      <div className={`mt-6 flex items-start gap-3 rounded-lg border p-4 ${healthy ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="owner-health">
        {healthy ? <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
        <div className="text-sm">
          <div className="font-semibold">{healthy ? "All systems healthy" : "Attention: issues detected (auto-retrying)"}</div>
          <div className="text-muted-foreground mt-0.5">
            Server errors (24h): {ov.errors_24h} · Undelivered emails: {ov.failed_emails} · Emails sent: {ov.emails_sent}
            {ov.last_cron && <> · Last automation run: {fmtDateTime(ov.last_cron.at)}</>}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><MessageSquareWarning className="h-3.5 w-3.5" /> Support tickets</div>
        {ov.tickets.length === 0 && <div className="text-sm text-muted-foreground">No tickets. The support assistant is handling questions.</div>}
        <div className="space-y-2">
          {ov.tickets.slice(0, 10).map((t) => (
            <div key={t.id} className="border border-border rounded-md p-3 text-sm" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium">{t.subject}</div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resolve(t.id, t.status === "open" ? "resolved" : "open")}>{t.status === "open" ? "Mark resolved" : "Reopen"}</Button>
              </div>
              <div className="text-xs text-muted-foreground">{t.email} · {t.plan} · {fmtDateTime(t.created_at)} · {t.status}</div>
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{t.message}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Recent subscriptions</div>
        {earn.transactions.length === 0 && <div className="text-sm text-muted-foreground">No paid subscriptions yet.</div>}
        {earn.transactions.slice(0, 8).map((t) => (
          <div key={t.session_id} className="flex justify-between items-center border-b border-border last:border-0 py-2 text-sm">
            <div><div className="font-medium capitalize">{t.lookup_key?.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</div></div>
            <div className="font-display font-bold tabular-nums">{money((t.amount || 0) / 100)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}
